import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from 'src/api/rpc/client';
import type { CreatePageSectionInput, UpdatePageSectionInput } from '@erp/rpc';

/**
 * Hooks over org-created sidebar page sections (`pageSections.*`). One list
 * serves both the sidebar (section titles for the pages it renders) and the
 * settings admin card. Archiving a section detaches the pages inside it, so
 * mutations also invalidate the task/agent page lists. Built-in pages placed
 * in sections come from their own org-wide readable list.
 */

function useInvalidatePageSections() {
  const queryClient = useQueryClient();
  return function invalidatePageSections() {
    void queryClient.invalidateQueries({
      queryKey: orpc.pageSections.list.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.pageSections.builtInPlacements.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.taskPages.list.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.agentPages.list.key(),
    });
  };
}

export function usePageSections(options?: { enabled?: boolean }) {
  return useQuery({
    ...orpc.pageSections.list.queryOptions(),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * The org sections holding built-in module pages, for the sidebar. Readable
 * by every org member, so it is fetched unconditionally.
 */
export function useBuiltInPagePlacements() {
  return useQuery({
    ...orpc.pageSections.builtInPlacements.queryOptions(),
    staleTime: 5 * 60 * 1000,
  });
}

/** Section id → name, for the admin tables' placement column. */
export function usePageSectionNameById(options?: {
  enabled?: boolean;
}): ReadonlyMap<string, string> {
  const { data } = usePageSections(options);
  return new Map((data ?? []).map((section) => [section.id, section.name]));
}

export function useCreatePageSection() {
  const invalidatePageSections = useInvalidatePageSections();
  return useMutation({
    mutationFn: (input: CreatePageSectionInput) =>
      rpcClient.pageSections.create(input),
    onSuccess: invalidatePageSections,
  });
}

export function useUpdatePageSection() {
  const invalidatePageSections = useInvalidatePageSections();
  return useMutation({
    mutationFn: (input: UpdatePageSectionInput) =>
      rpcClient.pageSections.update(input),
    onSuccess: invalidatePageSections,
  });
}

export function useArchivePageSection() {
  const invalidatePageSections = useInvalidatePageSections();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      rpcClient.pageSections.archive({ id }),
    onSuccess: invalidatePageSections,
  });
}
